import React, { useState, useEffect, useCallback } from "react";

// 96류 자동 생성 결정 트리 review — chapter 별 검토 + DB 활성화.
// 좌: chapter list (canonical set + validation 통계 + approved 상태)
// 우: chapter 상세 (heading → dimension → 값 ↔ hs6 매핑 + 사례 검증 신호) + approve/reject
//
// Google AI Studio 톤 (모노 + accent blue).

const C = {
  text: "#1f1f1f",
  textMuted: "#5f6368",
  textFaint: "#80868b",
  border: "#dadce0",
  borderSoft: "#e8eaed",
  bg: "#ffffff",
  bgSoft: "#f8f9fa",
  accent: "#1a73e8",
  accentBg: "#e8f0fe",
  green: "#188038",
  greenBg: "#e6f4ea",
  amber: "#b06000",
  amberBg: "#fef7e0",
};

const VALIDATION_META = {
  validated: { label: "검증됨", color: C.green, bg: C.greenBg },
  low_coverage: { label: "사례부족", color: C.textMuted, bg: C.bgSoft },
  low_relevance: { label: "키워드미일치", color: C.amber, bg: C.amberBg },
  no_precedent: { label: "사례없음", color: C.textFaint, bg: C.bgSoft },
  no_keywords: { label: "키워드없음", color: C.textFaint, bg: C.bgSoft },
};

const CHAPTER_NAMES = {}; // 선택: chapter 한글명 — 추후 CHAPTERS_97 연동

// ── T9: 5-digit 분류군 → 6-digit 트리 렌더러 ──────────────────────────────
function fmtHs5(d5) {
  // "41021" → "4102.1"
  return d5.length >= 5 ? `${d5.slice(0, 4)}.${d5.slice(4)}` : d5;
}
function fmtHs6(hs6) {
  // "410221" → "4102.21"
  return hs6.length >= 6 ? `${hs6.slice(0, 4)}.${hs6.slice(4)}` : hs6;
}
// "8443994"→"8443.99.4", "8443994010"→"8443.99.4010"
function fmtCode(prefix) {
  const s = String(prefix);
  if (s.length <= 4) return s;
  const p4 = s.slice(0, 4);
  if (s.length <= 6) return p4 + "." + s.slice(4);
  const p6 = s.slice(4, 6);
  const rest = s.slice(6);
  return p4 + "." + p6 + (rest ? "." + rest : "");
}

// ── T5: 국내세분(7→10) 재귀 렌더러 ─────────────────────────────────────────
function renderNationalTree(nodes, depth) {
  if (!nodes || nodes.length === 0) return null;
  const indent = 16 + depth * 14;
  return nodes.map((node, i) => {
    if (node.is_leaf) {
      return (
        <div
          key={node.hsk || i}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 10px",
            paddingLeft: indent,
            borderBottom: `1px solid ${C.borderSoft}`,
            fontSize: 12,
            background: depth % 2 === 0 ? "#fafbfc" : C.bg,
          }}
        >
          <code style={{ color: C.text, minWidth: 72, flexShrink: 0 }}>
            {fmtCode(node.hsk)}
          </code>
          <span style={{ color: C.text }}>{node.name_ko}</span>
        </div>
      );
    }
    // 그룹노드
    return (
      <div key={node.prefix || i}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 8,
            padding: "4px 10px",
            paddingLeft: indent,
            borderBottom: `1px solid ${C.borderSoft}`,
            fontSize: 12,
            background: C.bgSoft,
          }}
        >
          <code style={{ color: C.textMuted, minWidth: 72, flexShrink: 0 }}>
            {fmtCode(node.prefix)}
          </code>
          <span style={{ color: C.textMuted, fontStyle: "italic" }}>
            {node.label || fmtCode(node.prefix)}
          </span>
        </div>
        {renderNationalTree(node.children, depth + 1)}
      </div>
    );
  });
}

function renderHierarchy(h) {
  const hier = h.hierarchy;
  const axisLabel = hier.dimension_label_ko || "1차 분류군";
  const isExplanatory = hier.dash_source === "explanatory";

  return (
    <div>
      {/* axis label chip + source badge row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "6px 12px",
          borderBottom: `1px solid ${C.borderSoft}`,
        }}
      >
        <span
          style={{
            fontSize: 12,
            padding: "2px 8px",
            borderRadius: 10,
            background: C.accentBg,
            color: C.accent,
            fontWeight: 500,
          }}
        >
          {axisLabel}
        </span>
        <span
          style={{
            fontSize: 12,
            padding: "1px 7px",
            borderRadius: 8,
            background: isExplanatory ? C.greenBg : C.amberBg,
            color: isExplanatory ? C.green : C.amber,
          }}
        >
          {isExplanatory ? "호해설" : "코드추정"}
        </span>
      </div>

      {/* level1 groups */}
      {hier.level1.map((g, gi) => (
        <div key={gi}>
          {/* 1차 row */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 12px",
              borderBottom: `1px solid ${C.borderSoft}`,
              fontSize: 12,
            }}
          >
            <span
              style={{
                fontSize: 12,
                padding: "1px 6px",
                borderRadius: 8,
                background: C.bgSoft,
                color: C.textMuted,
                fontWeight: 500,
                flexShrink: 0,
              }}
            >
              1차
            </span>
            <code style={{ color: C.text, fontWeight: 600, minWidth: 52 }}>
              {fmtHs5(g.d5)}
            </code>
            <span style={{ flex: 1, color: C.text }}>{g.diff}</span>
            {g.is_terminal && g.terminal_hs6 && (
              <span style={{ fontSize: 12, color: C.textMuted }}>
                → {fmtHs6(g.terminal_hs6)}
              </span>
            )}
          </div>
          {/* T5: terminal 호 국내세분 */}
          {g.is_terminal && g.national_tree && (
            <div
              style={{
                marginLeft: 18,
                borderLeft: `2px solid ${C.borderSoft}`,
              }}
            >
              {renderNationalTree(g.national_tree, 0)}
            </div>
          )}

          {/* 2차 children (non-terminal) */}
          {!g.is_terminal && g.children && g.children.length > 0 && (
            <div
              style={{
                marginLeft: 18,
                borderLeft: `2px solid ${C.borderSoft}`,
              }}
            >
              {g.level2_label_ko && (
                <div
                  style={{
                    padding: "3px 10px",
                    fontSize: 12,
                    color: C.textFaint,
                  }}
                >
                  {g.level2_label_ko}
                </div>
              )}
              {g.children.map((c, ci) => (
                <React.Fragment key={ci}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "5px 10px",
                      borderBottom: `1px solid ${C.borderSoft}`,
                      fontSize: 12,
                    }}
                  >
                    <span
                      style={{
                        fontSize: 12,
                        padding: "1px 6px",
                        borderRadius: 8,
                        background: C.bgSoft,
                        color: C.textMuted,
                        flexShrink: 0,
                      }}
                    >
                      2차
                    </span>
                    <code
                      style={{ color: C.text, fontWeight: 600, minWidth: 52 }}
                    >
                      {fmtHs6(c.hs6)}
                    </code>
                    <span style={{ flex: 1, color: C.text }}>{c.diff}</span>
                  </div>
                  {/* T5: 6자리 소호 아래 국내세분(7→10) */}
                  {c.national_tree && renderNationalTree(c.national_tree, 0)}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function authHeaders(json = false) {
  const token =
    localStorage.getItem("token") ||
    sessionStorage.getItem("token") ||
    localStorage.getItem("admin_token");
  return {
    ...(json ? { "Content-Type": "application/json" } : {}),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function DraftCard({ item }) {
  if (!item?.draft) return null;
  const d = item.draft;
  const coverage = d.coverage || {};
  return (
    <div
      style={{
        margin: "10px 12px",
        padding: 12,
        border: `1px solid ${C.border}`,
        borderLeft: `3px solid ${C.amber}`,
        borderRadius: 6,
        background: "#fffdf7",
      }}
    >
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <strong>GPT 검토 초안</strong>
        <span style={{ color: C.amber, fontSize: 12 }}>검토 대기</span>
        <span style={{ color: C.textFaint, fontSize: 11 }}>
          {item.model} · {item.created_at}
        </span>
      </div>
      <div style={{ marginTop: 8, fontWeight: 600 }}>{d.question_ko}</div>
      {d.decision_basis_ko && (
        <div style={{ marginTop: 5, color: C.textMuted }}>{d.decision_basis_ko}</div>
      )}
      <div style={{ marginTop: 10, display: "grid", gap: 7 }}>
        {(d.branches || []).map((branch, index) => (
          <div
            key={`${branch.label_ko}-${index}`}
            style={{ padding: 8, border: `1px solid ${C.borderSoft}`, background: C.bg }}
          >
            <div style={{ fontWeight: 600 }}>
              {branch.label_ko || `분기 ${index + 1}`}
              <code style={{ marginLeft: 8, color: C.accent }}>
                {(branch.codes || []).map(fmtCode).join(", ")}
              </code>
            </div>
            {branch.question_ko && <div style={{ marginTop: 4 }}>{branch.question_ko}</div>}
            {(branch.required_facts || []).length > 0 && (
              <div style={{ marginTop: 4, color: C.textMuted }}>
                필수 확인: {branch.required_facts.join(" · ")}
              </div>
            )}
            {(branch.exclusion_facts || []).length > 0 && (
              <div style={{ marginTop: 3, color: C.textFaint }}>
                배제 조건: {branch.exclusion_facts.join(" · ")}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ marginTop: 8, fontSize: 11, color: C.textMuted }}>
        코드 커버리지 {coverage.covered_code_count ?? 0}/
        {coverage.source_code_count ?? 0}
        {coverage.complete ? " · 완전" : " · 누락/중복 검토 필요"}
      </div>
      {(d.warnings || []).length > 0 && (
        <div style={{ marginTop: 6, color: C.amber, fontSize: 12 }}>
          확인 필요: {d.warnings.join(" · ")}
        </div>
      )}
      <div style={{ marginTop: 7, color: C.textFaint, fontSize: 11 }}>
        GPT 생성 초안이며 검토 전에는 분류 또는 DB 활성화에 사용되지 않습니다.
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────────────────────────

export default function OntologyReviewPage({ embedded = false }) {
  const [chapters, setChapters] = useState([]);
  const [selectedCh, setSelectedCh] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [acting, setActing] = useState(false);
  const [drafts, setDrafts] = useState({});
  const [frames, setFrames] = useState({});
  const [generatingHs4, setGeneratingHs4] = useState(null);
  const [pipelineJob, setPipelineJob] = useState(null);
  const [startingPipeline, setStartingPipeline] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadChapters = useCallback(async () => {
    setLoadingList(true);
    try {
      const r = await fetch("/api/onto/review/chapters");
      const d = await r.json();
      setChapters(d.chapters || []);
    } catch (e) {
      setMsg(`목록 로드 실패: ${e.message}`);
    } finally {
      setLoadingList(false);
    }
  }, []);

  useEffect(() => {
    loadChapters();
  }, [loadChapters]);

  useEffect(() => {
    fetch("/api/onto/decision-tree-frames/jobs", { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        const latest = body?.jobs?.[0];
        if (latest) setPipelineJob(latest);
      })
      .catch(() => {});
  }, []);

  const loadDetail = useCallback(async (ch) => {
    setSelectedCh(ch);
    setDetail(null);
    setLoadingDetail(true);
    try {
      const r = await fetch(`/api/onto/review/chapter/${ch}`);
      const d = await r.json();
      setDetail(d);
      try {
        const dr = await fetch(`/api/onto/decision-tree-drafts/${ch}`, {
          headers: authHeaders(),
        });
        if (dr.ok) {
          const body = await dr.json();
          setDrafts(
            Object.fromEntries((body.drafts || []).map((item) => [item.hs4, item])),
          );
        } else {
          setDrafts({});
        }
      } catch (_) {
        setDrafts({});
      }
      try {
        const fr = await fetch(`/api/onto/decision-tree-frames/${ch}`, { headers: authHeaders() });
        if (fr.ok) {
          const body = await fr.json();
          setFrames(Object.fromEntries((body.frames || []).map((item) => [item.hs4, item])));
        } else setFrames({});
      } catch (_) {
        setFrames({});
      }
    } catch (e) {
      setMsg(`상세 로드 실패: ${e.message}`);
    } finally {
      setLoadingDetail(false);
    }
  }, []);

  const generateDraft = useCallback(async (ch, hs4) => {
    setGeneratingHs4(hs4);
    setMsg(`${hs4}호의 공식 구조를 분석하고 있습니다…`);
    try {
      const r = await fetch(
        `/api/onto/decision-tree-drafts/${ch}/${hs4}/generate`,
        { method: "POST", headers: authHeaders(true) },
      );
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.message || body.error || `HTTP ${r.status}`);
      setDrafts((prev) => ({ ...prev, [hs4]: body }));
      setMsg(`${hs4}호 GPT 초안을 검토 대기로 저장했습니다. 활성 트리에는 반영되지 않았습니다.`);
    } catch (e) {
      setMsg(`${hs4}호 GPT 초안 생성 실패: ${e.message}`);
    } finally {
      setGeneratingHs4(null);
    }
  }, []);

  const startPipeline = useCallback(async (scope) => {
    const confirmation = scope === "all"
      ? "전체 96류의 공식 계층 골격을 생성합니다. 모델 API를 호출하지 않아 비용이 들지 않습니다. 시작할까요?"
      : String(scope).length === 4
        ? `${scope}호의 공식 계층 골격만 API 비용 없이 다시 생성할까요? 기존 질문·독립 검증 승인은 source hash가 바뀔 때 자동 무효화됩니다.`
        : `${scope}류의 공식 계층 골격을 API 비용 없이 생성할까요?`;
    if (!window.confirm(confirmation)) return;
    setStartingPipeline(true);
    try {
      const r = await fetch("/api/onto/decision-tree-frames/jobs", {
        method: "POST",
        headers: authHeaders(true),
        body: JSON.stringify({ scope }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(body.message || body.error || `HTTP ${r.status}`);
      setPipelineJob(body);
      setMsg(`${scope === "all" ? "전체" : scope} 공식 다단계 프레임 생성을 시작했습니다. 코드와 부모-자식 관계만 만들며 LLM API는 호출하지 않습니다.`);
    } catch (e) {
      setMsg(`공식 프레임 생성 실패: ${e.message}`);
    } finally {
      setStartingPipeline(false);
    }
  }, []);

  useEffect(() => {
    if (!pipelineJob?.id || ![
      "queued", "running",
    ].includes(pipelineJob.status)) return undefined;
    const timer = setInterval(async () => {
      try {
        const r = await fetch(`/api/onto/decision-tree-frames/jobs/${pipelineJob.id}`, {
          headers: authHeaders(),
        });
        if (r.ok) setPipelineJob(await r.json());
      } catch (_) {}
    }, 3000);
    return () => clearInterval(timer);
  }, [pipelineJob?.id, pipelineJob?.status]);

  useEffect(() => {
    if (!selectedCh || !["completed", "completed_with_errors"].includes(pipelineJob?.status)) return;
    fetch(`/api/onto/decision-tree-frames/${selectedCh}`, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : null))
      .then((body) => {
        if (body) setFrames(Object.fromEntries((body.frames || []).map((item) => [item.hs4, item])));
      })
      .catch(() => {});
  }, [pipelineJob?.status, selectedCh]);

  const doApprove = useCallback(
    async (ch) => {
      if (
        !window.confirm(`chapter ${ch} 를 활성화 (DB seed) 합니다. 진행할까요?`)
      )
        return;
      setActing(true);
      setMsg(null);
      try {
        const r = await fetch(`/api/onto/review/approve/${ch}`, {
          method: "POST",
        });
        const d = await r.json();
        if (d.ok) {
          setMsg(`✓ ${d.message}`);
          loadChapters();
        } else {
          setMsg(`실패: ${d.error}`);
        }
      } catch (e) {
        setMsg(`approve 실패: ${e.message}`);
      } finally {
        setActing(false);
      }
    },
    [loadChapters],
  );

  const doReject = useCallback(
    async (ch) => {
      setActing(true);
      try {
        const r = await fetch(`/api/onto/review/reject/${ch}`, {
          method: "POST",
        });
        const d = await r.json();
        setMsg(d.ok ? `${d.message}` : `실패: ${d.error}`);
        loadChapters();
      } catch (e) {
        setMsg(`reject 실패: ${e.message}`);
      } finally {
        setActing(false);
      }
    },
    [loadChapters],
  );

  const wrapper = embedded
    ? { width: "100%", height: "100%" }
    : { width: "100vw", height: "100vh" };

  return (
    <div
      style={{
        ...wrapper,
        display: "grid",
        gridTemplateColumns: "300px 1fr",
        background: C.bgSoft,
        color: C.text,
        fontSize: 13,
      }}
    >
      {/* ─── 좌: chapter list ─── */}
      <div
        style={{
          background: C.bg,
          borderRight: `1px solid ${C.borderSoft}`,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            padding: "12px 14px",
            borderBottom: `1px solid ${C.borderSoft}`,
            fontWeight: 500,
            fontSize: 13,
          }}
        >
          결정 트리 검토 ({chapters.length}류)
          <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3 }}>
            자동 생성 → 검토 후 활성화
          </div>
        </div>
        <div style={{ flex: 1, overflow: "auto" }}>
          {loadingList && (
            <div style={{ padding: 14, color: C.textMuted, fontSize: 12 }}>
              로딩…
            </div>
          )}
          {chapters.map((c) => {
            const valPct = c.hs6_total
              ? Math.round((c.validated / c.hs6_total) * 100)
              : 0;
            const active = selectedCh === c.chapter;
            return (
              <div
                key={c.chapter}
                onClick={() => loadDetail(c.chapter)}
                style={{
                  padding: "9px 14px",
                  borderBottom: `1px solid ${C.borderSoft}`,
                  cursor: "pointer",
                  background: active ? C.accentBg : "transparent",
                  borderLeft: active
                    ? `3px solid ${C.accent}`
                    : "3px solid transparent",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                  }}
                >
                  <span style={{ fontWeight: 500 }}>
                    {c.chapter}류
                    {CHAPTER_NAMES[c.chapter]
                      ? ` ${CHAPTER_NAMES[c.chapter]}`
                      : ""}
                  </span>
                  {c.approved && (
                    <span
                      style={{
                        fontSize: 12,
                        color: C.green,
                        background: C.greenBg,
                        padding: "1px 6px",
                        borderRadius: 8,
                      }}
                    >
                      활성
                    </span>
                  )}
                </div>
                <div style={{ fontSize: 12, color: C.textMuted, marginTop: 3 }}>
                  {c.heading_count}호 · dim {c.canonical_set.length} · 검증{" "}
                  {valPct}%
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ─── 우: chapter 상세 ─── */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          background: C.bg,
        }}
      >
        {msg && (
          <div
            style={{
              padding: "8px 16px",
              fontSize: 12,
              background: C.accentBg,
              color: C.text,
              borderBottom: `1px solid ${C.borderSoft}`,
            }}
          >
            {msg}
          </div>
        )}
        {pipelineJob && (
          <div style={{ padding: "9px 16px", background: "#f8fbff", borderBottom: `1px solid ${C.borderSoft}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <strong>공식 다단계 프레임 v2</strong>
              <span>{pipelineJob.status}</span>
              {pipelineJob.annotation_model && (
                <span style={{ color: C.accent }}>질문 보강 {pipelineJob.annotation_model}</span>
              )}
              {pipelineJob.cross_verifier_model && (
                <span style={{ color: C.textMuted }}>교차검증 {pipelineJob.cross_verifier_model}</span>
              )}
              <span style={{ color: C.textMuted }}>
                {pipelineJob.processed || 0}/{pipelineJob.total || 0}
                {pipelineJob.current_hs4 ? ` · ${pipelineJob.current_hs4} 처리 중` : ""}
              </span>
              <span style={{ color: C.green }}>노드 {pipelineJob.node_count || 0}</span>
              <span style={{ color: C.accent }}>실제 분기점 {pipelineJob.decision_count || 0}</span>
              <span style={{ color: C.textMuted }}>세번 terminal {pipelineJob.terminal_count || 0}</span>
              <span style={{ color: C.green }}>API 비용 ₩{pipelineJob.api_cost || 0}</span>
              <span style={{ color: "#b3261e" }}>실패 {pipelineJob.failed || 0}</span>
            </div>
            <div style={{ marginTop: 6, height: 4, background: C.borderSoft, borderRadius: 2 }}>
              <div style={{ height: "100%", borderRadius: 2, background: C.accent,
                width: `${pipelineJob.total ? Math.min(100, (pipelineJob.processed / pipelineJob.total) * 100) : 0}%` }} />
            </div>
            {pipelineJob.error && (
              <div style={{ marginTop: 6, color: "#b3261e", fontSize: 12 }}>
                중단 사유: {pipelineJob.error}
              </div>
            )}
          </div>
        )}
        {!selectedCh && (
          <div
            style={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: C.textFaint,
              fontSize: 13,
            }}
          >
            좌측에서 류(chapter) 를 선택하세요.
          </div>
        )}
        {loadingDetail && (
          <div style={{ padding: 16, color: C.textMuted }}>로딩…</div>
        )}
        {detail && (
          <>
            {/* 상단 — chapter 헤더 + 액션 */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "12px 16px",
                borderBottom: `1px solid ${C.borderSoft}`,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 500 }}>
                {detail.chapter}류
              </div>
              <div style={{ fontSize: 12, color: C.textMuted }}>
                canonical: {detail.canonical_set.join(", ")}
              </div>
              <div style={{ flex: 1 }} />
              <button
                onClick={() => startPipeline(detail.chapter)}
                disabled={startingPipeline || ["queued", "running"].includes(pipelineJob?.status)}
                style={{ padding: "6px 11px", background: C.bg, color: C.accent,
                  border: `1px solid ${C.accent}`, borderRadius: 4, fontSize: 12 }}
              >
                이 류 공식 프레임 생성
              </button>
              <button
                onClick={() => startPipeline("all")}
                disabled={startingPipeline || ["queued", "running"].includes(pipelineJob?.status)}
                style={{ padding: "6px 11px", background: C.green, color: "#fff",
                  border: "none", borderRadius: 4, fontSize: 12 }}
              >
                전체 공식 프레임 생성 (무료)
              </button>
              <button
                onClick={() => doApprove(detail.chapter)}
                disabled={acting}
                style={{
                  padding: "6px 14px",
                  background: C.accent,
                  color: "#fff",
                  border: "none",
                  borderRadius: 4,
                  cursor: acting ? "wait" : "pointer",
                  fontSize: 12,
                  fontWeight: 500,
                }}
              >
                활성화 (DB seed)
              </button>
              <button
                onClick={() => doReject(detail.chapter)}
                disabled={acting}
                style={{
                  padding: "6px 12px",
                  background: C.bg,
                  color: C.textMuted,
                  border: `1px solid ${C.border}`,
                  borderRadius: 4,
                  cursor: "pointer",
                  fontSize: 12,
                }}
              >
                reject
              </button>
            </div>

            {/* heading 별 결정 트리 */}
            <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
              {detail.headings.map((h) => (
                <div
                  key={h.hs4}
                  style={{
                    marginBottom: 16,
                    border: `1px solid ${C.borderSoft}`,
                    borderRadius: 8,
                    overflow: "hidden",
                  }}
                >
                  {/* heading 헤더 — dimension */}
                  <div
                    style={{
                      padding: "8px 12px",
                      background: C.bgSoft,
                      borderBottom: `1px solid ${C.borderSoft}`,
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    <code style={{ fontWeight: 600, color: C.text }}>
                      {h.hs4}
                    </code>
                    <span style={{ fontSize: 12, color: C.textMuted }}>
                      질문:
                    </span>
                    {h.dimensions.map((d, i) => (
                      <span
                        key={i}
                        style={{
                          fontSize: 12,
                          padding: "2px 8px",
                          borderRadius: 10,
                          background: C.accentBg,
                          color: C.accent,
                          fontWeight: 500,
                        }}
                      >
                        {d.label_ko}
                      </span>
                    ))}
                    {h.raw_label && (
                      <span style={{ fontSize: 12, color: C.textFaint }}>
                        (raw: {h.raw_label})
                      </span>
                    )}
                    {frames[h.hs4] && (
                      <span style={{ fontSize: 11, color: C.green, background: C.greenBg,
                        padding: "2px 7px", borderRadius: 9 }}>
                        v2 {frames[h.hs4].decision_count}분기 · {frames[h.hs4].terminal_count}세번 · {frames[h.hs4].annotation_status}
                        {frames[h.hs4].warning_count > 0 ? ` · ⚠ 소스불일치 ${frames[h.hs4].warning_count}` : ""}
                      </span>
                    )}
                    <div style={{ flex: 1 }} />
                    <button
                      onClick={() => startPipeline(h.hs4)}
                      disabled={startingPipeline || ["queued", "running"].includes(pipelineJob?.status)}
                      title="이 HS4의 공식 v2 프레임만 다시 생성합니다. 모델 API는 호출하지 않습니다."
                      style={{
                        padding: "4px 9px",
                        border: `1px solid ${C.accent}`,
                        borderRadius: 4,
                        background: C.bg,
                        color: C.accent,
                        cursor: startingPipeline ? "wait" : "pointer",
                        fontSize: 11,
                      }}
                    >
                      이 호 v2 재생성
                    </button>
                    <button
                      onClick={() => generateDraft(detail.chapter, h.hs4)}
                      disabled={!!generatingHs4}
                      title="기존 호당 단일질문 API 초안입니다. 유료이며 v2 다단계 프레임에는 사용하지 않습니다."
                      style={{
                        padding: "4px 9px",
                        border: `1px solid ${C.border}`,
                        borderRadius: 4,
                        background: C.bg,
                        color: generatingHs4 === h.hs4 ? C.textMuted : C.accent,
                        cursor: generatingHs4 ? "wait" : "pointer",
                        fontSize: 11,
                      }}
                    >
                      {generatingHs4 === h.hs4
                        ? "GPT 초안 생성 중…"
                        : drafts[h.hs4]
                          ? "기존 유료 초안 다시 생성"
                          : "기존 단일질문 API (유료)"}
                    </button>
                  </div>
                  <DraftCard item={drafts[h.hs4]} />
                  {/* 값 ↔ hs6 매핑 + 검증 신호 — hierarchy or flat fallback */}
                  {h.hierarchy && h.hierarchy.level1?.length ? (
                    renderHierarchy(h)
                  ) : (
                    <div>
                      {h.values.map((v, i) => {
                        const vm =
                          VALIDATION_META[v.validation] ||
                          VALIDATION_META.no_precedent;
                        return (
                          <div
                            key={i}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              padding: "6px 12px",
                              borderBottom:
                                i < h.values.length - 1
                                  ? `1px solid ${C.borderSoft}`
                                  : "none",
                              fontSize: 12,
                            }}
                          >
                            <code
                              style={{
                                color: C.text,
                                fontWeight: 600,
                                minWidth: 60,
                              }}
                            >
                              {v.hs6}
                            </code>
                            <span style={{ flex: 1, color: C.text }}>
                              {v.diff}
                            </span>
                            {v.precedent_count != null && (
                              <span
                                style={{ fontSize: 12, color: C.textMuted }}
                              >
                                사례 {v.precedent_count}
                                {v.relevance != null
                                  ? ` · ${Math.round(v.relevance * 100)}%`
                                  : ""}
                              </span>
                            )}
                            <span
                              style={{
                                fontSize: 12,
                                padding: "1px 7px",
                                borderRadius: 8,
                                background: vm.bg,
                                color: vm.color,
                                minWidth: 56,
                                textAlign: "center",
                              }}
                            >
                              {vm.label}
                            </span>
                          </div>
                        );
                      })}
                      {h.wildcards.length > 0 && (
                        <div
                          style={{
                            padding: "5px 12px",
                            fontSize: 12,
                            color: C.textFaint,
                            background: C.bgSoft,
                          }}
                        >
                          기타/wildcard: {h.wildcards.join(", ")}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
